import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { api } from '../services/api';
import { buildRowQueryParams } from './Home';

/**
 * Calendário compacto
 * - Tela cheia por padrão
 * - Divide em 2 colunas quando um dia é selecionado
 * - Marcadores: verde (a receber), vermelho (a pagar), ignora concluídos
 * - Clique em lançamento => /lancamentos?date=YYYY-MM-DD
 */
export default function Calendario() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null); // number | null

  // ---------- Data load ----------
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await api.getLancamentos(); // mantém sua fonte de dados
        setLancamentos(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erro ao carregar lançamentos:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---------- Helpers ----------
  const y = currentDate.getFullYear();
  const m = currentDate.getMonth();

  const { daysInMonth, startingDayOfWeek } = useMemo(() => {
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    return {
      daysInMonth: lastDay.getDate(),
      startingDayOfWeek: firstDay.getDay(), // 0..6
    };
  }, [y, m]);

  const pad2 = (n) => String(n).padStart(2, '0');
  const isoForDay = (day) => `${y}-${pad2(m + 1)}-${pad2(day)}`;

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      Number(value || 0)
    );

  // Lista completa do dia (para o painel)
  const getAllDoDia = (day) => {
    const iso = isoForDay(day);
    return lancamentos.filter((l) => l.data_vencimento === iso);
  };

  // Somente itens que viram "chips" no calendário (sem concluídos)
  const getMarcadoresDoDia = (day) => {
    const iso = isoForDay(day);
    return lancamentos.filter(
      (l) =>
        l.data_vencimento === iso &&
        String(l.status).toLowerCase() !== 'pago' // ignora concluídos no grid
    );
  };

  const previousMonth = () => {
    setCurrentDate(new Date(y, m - 1, 1));
    setSelectedDay(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(y, m + 1, 1));
    setSelectedDay(null);
  };

  const goToLancamentos = (params = {}) => {
    const sanitized = Object.entries(params).filter(([, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== null && value !== '';
    });

    const searchParams = new URLSearchParams();
    sanitized.forEach(([key, value]) => {
      if (Array.isArray(value)) {
        searchParams.set(key, value.join(','));
      } else {
        searchParams.set(key, String(value));
      }
    });

    const query = searchParams.toString();
    navigate(query ? `/lancamentos?${query}` : '/lancamentos');
  };

  const openLancamentos = (isoDate, lancamento = null) => {
    if (!isoDate) {
      goToLancamentos();
      return;
    }

    const baseParams = {
      data_inicio: isoDate,
      data_fim: isoDate,
    };

    if (lancamento) {
      const rowParams = buildRowQueryParams(lancamento);
      Object.assign(baseParams, rowParams || {});
      baseParams.data_inicio = isoDate;
      baseParams.data_fim = isoDate;
    }

    goToLancamentos(baseParams);
  };

  // ---------- UI ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-transparent mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Carregando calendário…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Título */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Calendário</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Visualize seus lançamentos por data</p>
      </div>

      {/* Cabeçalho com seletor Mês/Ano */}
      <div className="card p-3">
        <div className="flex items-center justify-between">
          <button
            onClick={previousMonth}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Mês anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            {/* Mês */}
            <select
              value={m}
              onChange={(e) => setCurrentDate(new Date(y, parseInt(e.target.value, 10), 1))}
              className="bg-transparent text-lg font-semibold capitalize focus:outline-none"
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <option
                  key={i}
                  value={i}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                >
                  {new Date(0, i).toLocaleString('pt-BR', { month: 'long' })}
                </option>
              ))}
            </select>

            {/* Ano (janela atual-5 … atual+5) */}
            <select
              value={y}
              onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value, 10), m, 1))}
              className="bg-transparent text-lg font-semibold focus:outline-none"
            >
              {Array.from({ length: 11 }).map((_, i) => {
                const base = new Date().getFullYear();
                const year = base - 5 + i;
                return (
                  <option
                    key={year}
                    value={year}
                    className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  >
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          <button
            onClick={nextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Próximo mês"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Layout condicional: tela cheia ou dividido */}
      <div className={selectedDay ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'grid grid-cols-1'}>
        {/* Calendário */}
        <div className="card p-4">
          {/* Cabeçalho do mês (somente rótulo auxiliar para desktop) */}
          <div className="hidden md:flex items-center justify-center mb-2">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 capitalize">
              {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
            </span>
          </div>

          {/* Dias da semana */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d) => (
              <div
                key={d}
                className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-1"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Dias */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: startingDayOfWeek }).map((_, i) => (
              <div key={`blank-${i}`} className="aspect-square" />
            ))}

            {Array.from({ length: daysInMonth }).map((_, idx) => {
              const day = idx + 1;
              const iso = isoForDay(day);
              const marcadores = getMarcadoresDoDia(day); // já ignora concluídos
              const has = marcadores.length > 0;

              const today = new Date();
              const isToday =
                day === today.getDate() && m === today.getMonth() && y === today.getFullYear();
              const isSelected = selectedDay === day;

              return (
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={[
                    'aspect-square rounded-lg border text-left p-1.5 transition-all',
                    isSelected
                      ? 'border-primary-500 ring-2 ring-primary-500/40'
                      : 'border-gray-200 dark:border-gray-700',
                    isToday ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-white dark:bg-gray-800',
                    'hover:border-primary-400',
                  ].join(' ')}
                  title={`Abrir dia ${pad2(day)}/${pad2(m + 1)}/${y}`}
                >
                  <div className="flex flex-col h-full">
                    <div
                      className={[
                        'text-[11px] font-bold mb-1',
                        isToday
                          ? 'text-primary-700 dark:text-primary-300'
                          : 'text-gray-800 dark:text-white',
                      ].join(' ')}
                    >
                      {day}
                    </div>

                    {/* Chips dos lançamentos (sem concluídos) */}
                    {has && (
                      <div className="flex-1 overflow-hidden space-y-1">
                        {marcadores.slice(0, 2).map((l, i2) => {
                          // Normalizar tipo:
                          // - 'receita' | 'despesa'  ou  'receber' | 'pagar'
                          const tipo = String(l.tipo).toLowerCase();
                          const isReceber = tipo === 'receita' || tipo === 'receber';
                          const chipClass = isReceber
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';

                          return (
                            <div
                              key={i2}
                              className={['truncate px-1 py-0.5 rounded text-[10px] font-medium', chipClass].join(
                                ' '
                              )}
                              title={l.descricao}
                            >
                              {l.descricao}
                            </div>
                          );
                        })}
                        {marcadores.length > 2 && (
                          <div className="text-[10px] text-gray-500 dark:text-gray-400">+{marcadores.length - 2} mais</div>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Painel do dia selecionado (só aparece quando houver dia) */}
        {selectedDay && (
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Lançamentos de {pad2(selectedDay)}/{pad2(m + 1)}/{y}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => openLancamentos(isoForDay(selectedDay))}
                  className="text-sm px-3 py-1.5 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  title="Abrir em Lançamentos com filtro desta data"
                >
                  Abrir em Lançamentos
                </button>
                <button
                  onClick={() => setSelectedDay(null)}
                  className="text-sm px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  title="Fechar painel"
                >
                  Fechar
                </button>
              </div>
            </div>

            {getAllDoDia(selectedDay).length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Nenhum lançamento neste dia
              </div>
            ) : (
              <div className="space-y-2">
                {getAllDoDia(selectedDay).map((l) => {
                  const tipo = String(l.tipo).toLowerCase();
                  const isReceber = tipo === 'receita' || tipo === 'receber';
                  const status = String(l.status || '').toLowerCase();

                  // cores de valor por tipo
                  const valorClass = isReceber
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-rose-600 dark:text-rose-400';

                  // badge de status
                  const statusBadge = (() => {
                    if (status === 'pago')
                      return 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30';
                    if (status === 'parcial' || status === 'parcialmente_pago')
                      return 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30';
                    if (status === 'pendente')
                      return 'bg-blue-500/15 text-blue-400 border border-blue-500/30';
                    return 'bg-gray-500/15 text-gray-400 border border-gray-500/30';
                  })();

                  return (
                    <button
                      key={l.id}
                      onClick={() => openLancamentos(l.data_vencimento, l)}
                      className="w-full text-left rounded-lg px-3 py-3 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">{l.descricao}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={[
                                'px-2 py-0.5 rounded-full text-[11px] font-medium',
                                isReceber
                                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                  : 'bg-rose-500/15 text-rose-400 border border-rose-500/30',
                              ].join(' ')}
                            >
                              {isReceber ? 'A Receber' : 'A Pagar'}
                            </span>
                            {status && (
                              <span className={['px-2 py-0.5 rounded-full text-[11px] font-medium', statusBadge].join(' ')}>
                                {status === 'pago'
                                  ? 'Concluída'
                                  : status === 'parcial' || status === 'parcialmente_pago'
                                  ? 'Parcial'
                                  : 'Pendente'}
                              </span>
                            )}
                            {l.categoria && (
                              <span className="text-[11px] text-gray-500 dark:text-gray-400">{l.categoria}</span>
                            )}
                          </div>
                        </div>
                        <div className={['text-sm font-bold', valorClass].join(' ')}>
                          {formatCurrency(l.valor)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
